import { h, render } from 'preact';
import Beans3 from '@/beans';
import InactivePage from '@/base/page/InactivePage';
import { i18n, Utils, riperStorage } from '@/base/helpers';
import { Widget, Tabulator, Flow } from './helpers';
import { CartButtonRenderer, ProductButtonRenderer, CheckoutButtonRenderer } from './views/buttons';
import ActivePage from './views/page/ActivePage';

const Liana = {
  Actions: {
    onRegister: () => riperStorage.onRegister(),
    onLogin: () => riperStorage.onLogin(),
    onAbout: () => riperStorage.onAbout(),
  },
  async init({ args, riperDisplay: display, account }) {
    if (args.Redemption) Liana.Redemption = args.Redemption;

    // Fetch and set VIP tiers before rendering
    try {
      const lattice = await Beans3.get({
        path: `lattice/merchant/current/`,
        namespace: 'trellis',
      });
      const merchantTiers = Utils.convertVIPTiersToArray(lattice.vip);
      display.tiers = merchantTiers;
      if (riperStorage.riperDisplay) {
        riperStorage.riperDisplay.tiers = merchantTiers;
      }

      // If authenticated, fetch and set shopper tier
      if (account) {
        let shopperTierSet = false;

        try {
          const vipShopper = await Beans3.get({
            path: `vip/shopper/${account.id}/`,
            namespace: 'trellis',
          });
          const transactions = await Beans3.get({ path: 'ultimate/account/current/history?limit=30' });
          const shopperActiveTier = merchantTiers.find((tier) => tier.level === vipShopper?.tier_level) || null;

          if (vipShopper && shopperActiveTier) {
            const nextTierMilestone = Utils.getNextTierMilestone(merchantTiers, shopperActiveTier.level);

            account.tier = {
              ...shopperActiveTier,
              tier_next_milestone: nextTierMilestone?.milestone,
              tier_next_name: nextTierMilestone?.name,
            };
            account.tier_expiring = vipShopper.tier_expiring_date;
            account.tier_started = vipShopper.tier_started;
            account.tier_beans_rolling = vipShopper.tier_beans_cumulative;
            account.tier_beans_fixed = Utils.calculateTierBeansFixed(account, transactions.data);
            riperStorage.account = account;
            shopperTierSet = true;
          }
        } catch (err) {
          console.error('Failed to fetch shopper tier:', err);
        }

        if (!shopperTierSet) {
          account.tier = Utils.getCurrentTier(account, display);
        }
      }
    } catch (err) {
      console.error('Failed to fetch VIP tiers:', err);
      // Fallback to existing tiers if fetch fails
      if (account) account.tier = Utils.getCurrentTier(account, display);
    }

    Flow.init(account);

    if (display.is_active || Beans3.previewSession.isActive()) {
      Beans3.require('snow').then(Widget.init);
      Liana.View.renderCart();
      if (riperStorage.currentPage === 'checkout') Liana.View.renderCheckout();
    }

    Liana.View.renderPage();
  },
};

Liana.View = {
  ProductButtonRenderer,
  CartButtonRenderer,
  CheckoutButtonRenderer,

  setCheckoutContainers() {
    /** Checkout Page
     * Display the option for shoppers to redeem points on Checkout.
     */
    function _setContainers(resolve) {
      const setCheckoutContainers = (iteration) => {
        const checkoutContainers = CheckoutButtonRenderer.setContainers();
        if (checkoutContainers.length) {
          resolve(checkoutContainers);
        } else if (iteration < 3) {
          setTimeout(() => setCheckoutContainers(iteration + 1), 2000);
        } else {
          resolve([]);
        }
      };
      setCheckoutContainers(0);
    }
    return Utils.createDOMPromise(_setContainers);
  },
  setProductContainers() {
    function _setContainers(resolve) {
      /** Product Page
       * Display the option for shoppers to buy product with points from the product view page.
       * There can be a cart drawer on product, so not resolve after retrieving product containers.
       */
      // if (riperStorage.currentPage === 'product' && riperStorage.riperDisplay.is) {
      //   ProductButtonRenderer.setContainers();
      // }
      resolve([]);
    }
    return Utils.createDOMPromise(_setContainers);
  },
  setCartContainers() {
    function _setContainers(resolve) {
      /** Cart Page or Cart Drawer
       * Display the option for shoppers to redeem points from the cart Page or cart drawer;
       */
      const setCartContainers = (iteration) => {
        const cartContainers = CartButtonRenderer.setContainers();
        if (cartContainers.length) {
          resolve(cartContainers);
        } else if (iteration < 3) {
          setTimeout(() => setCartContainers(iteration + 1), 2000);
        } else {
          resolve([]);
        }
      };
      setCartContainers(0);
    }

    return Utils.createDOMPromise(_setContainers);
  },
  setPageContainer() {
    /** Rewards Program Page
     * Display the rewards program page.
     */
    function _setContainers(resolve) {
      const setPageContainers = (iteration) => {
        const pageContainer = riperStorage.args.pagePlaceholder || document.getElementById('liana-rewards-page');
        if (pageContainer) {
          resolve(pageContainer);
        } else if (iteration < 3) {
          setTimeout(() => setPageContainers(iteration + 1), 2000);
        } else {
          resolve(null);
        }
      };
      setPageContainers(0);
    }
    return Utils.createDOMPromise(_setContainers);
  },
  renderCart() {
    return this.setCartContainers()
      .then(() => CartButtonRenderer.render())
      .then(Liana.Redemption.load);
  },
  renderCheckout() {
    return this.setCheckoutContainers()
      .then(() => CheckoutButtonRenderer.render(Liana.Redemption.CheckoutButtonComponent))
      .then(Liana.Redemption.load);
  },
  renderProduct() {
    return this.setProductContainers()
      .then(() => ProductButtonRenderer.render())
      .then(Liana.Redemption.load);
  },
  renderPage() {
    return this.setPageContainer().then((pagePlaceholder) => {
      if (pagePlaceholder) {
        const display = riperStorage.riperDisplay;
        const account = riperStorage.account;
        pagePlaceholder.innerHTML = '';
        const Strings = Utils.parseI18n(i18n._Strings, Utils.getI18nData(display, account));
        if (display.is_active || Beans3.previewSession.isActive()) {
          const tabulator = new Tabulator(account, display.tiers.length > 1);

          render(
            <ActivePage
              display={display}
              account={account}
              tabulator={tabulator}
              Strings={Strings}
              onRegister={() => Flow.redirectOnAuthToProgramPage(riperStorage.onRegister)}
              onLogin={() => Flow.redirectOnAuthToProgramPage(riperStorage.onLogin)}
            />,
            pagePlaceholder
          );
        } else {
          render(<InactivePage message={Strings.page.inactive} />, pagePlaceholder);
        }
      }
    });
  },
};

export default Liana;
